import { useCallback, useLayoutEffect } from 'react';
import { useAuth } from 'react-oidc-context';

import type { RouterEvents, ParsedLocation } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';

import { useIntlStore } from '@dcentralexpedientedigital/fe-shared/stores/intl-store';
import { useModalViewStore } from '@dcentralexpedientedigital/fe-shared/stores/modal-view-store';
import { useNavigationStore } from '@dcentralexpedientedigital/fe-shared/stores/navigation-store';
import type { IStaticBlocksData } from '@dcentralexpedientedigital/fe-shared/types/config.interface';
import { useScrollIntoView } from '@dcentralexpedientedigital/fe-ui/hooks/use-scroll-into-view';

import { isBlocks } from '../utils/is-blocks';
import { pathToSearchParam } from '../utils/path-to-search-param';

export interface UrlSearchSchema {
    modal: string;
}

export const useRouteInterceptor = () => {
    const router = useRouter();
    const scrollIntoView = useScrollIntoView();
    const setPreviousPath = useNavigationStore((state) => state.setPreviousPath);
    const setPreviousLocation = useNavigationStore((state) => state.setPreviousLocation);
    const auth = useAuth();
    const locale = useIntlStore((state) => state.locale);

    const getRoute = useCallback(
        (
            pathname: string,
            search: Record<string, string> | UrlSearchSchema,
        ): IStaticBlocksData | undefined => {
            const matchedRoutes = router.matchRoutes(pathname, search);
            return matchedRoutes.find(({ staticData }) => isBlocks(staticData))
                ?.staticData as IStaticBlocksData;
        },
        [router],
    );

    const openModalIfRequired = useCallback(
        (modalPath: string) => {
            const url = new URL(modalPath, window.location.href);
            const currentModalRoute = url.searchParams.get('modal');

            const modalState = useModalViewStore.getState();

            if (modalState.isOpen && modalState?.configData?.modalRoute === currentModalRoute) {
                return false;
            }

            const shouldRemoveLoginModal =
                currentModalRoute === 'login' &&
                auth?.isAuthenticated &&
                !auth?.isLoading &&
                auth?.user?.profile?.sub;

            if (shouldRemoveLoginModal) {
                const searchParams = new URLSearchParams(url.search);
                searchParams.delete('modal');
                const newSearch = Object.fromEntries(searchParams.entries());

                router.navigate({
                    to: url.pathname,
                    search: newSearch,
                    replace: true,
                });
                return false;
            }

            if (!currentModalRoute) {
                return false;
            }

            // TODO: We might as well just store the locale in the modal URL.
            const languageAppended = `/${locale}/${currentModalRoute}`;

            // Find the current modal and open it
            const matchedRoutes = router.matchRoutes(languageAppended, {});
            const route = matchedRoutes
                .filter((route) => route && route.pathname)
                .find((route) => route.pathname.endsWith(currentModalRoute))
                ?.staticData as IStaticBlocksData;

            if (!route || Object.keys(route).length === 0) {
                return;
            }

            if (!route?.openInModal) {
                return;
            }

            useModalViewStore.getState().openModal({
                name: route.name,
                modalRoute: currentModalRoute,
                requireAuth: route.requireAuth,
                blocks: route.blocks,
                header: route.header,
                options: {
                    variant:
                        typeof route.openInModal === 'boolean'
                            ? 'primary'
                            : (route.openInModal.variant ?? 'primary'),
                    shouldCloseOnEsc:
                        typeof route.openInModal === 'boolean'
                            ? true
                            : route.openInModal.shouldCloseOnEsc,
                },
                onCloseCallback() {
                    const search = router.history.location.search;

                    if (!search) {
                        return;
                    }

                    const query = new URLSearchParams(search);

                    // Check if we've already navigated, this resolves conflicts in navigation eg.
                    // when redirects happen outside of the modal eg. login.
                    // Note: This potentially could have issues if the route we navigate to has a modal
                    // query param. At which point we should resolve this so the interceptor does not handle
                    // this routing.
                    if (!query.has('modal')) {
                        return;
                    }

                    router.navigate({ to: url.pathname });
                },
            });
            return true;
        },
        [locale, auth, router],
    );

    const handleModalRouting = useCallback(
        async (toPath: ParsedLocation, fromPath?: ParsedLocation) => {
            const fallbackFrom = fromPath ?? router.state.location;
            const isSamePage = fallbackFrom?.pathname === toPath?.pathname;
            if (!fallbackFrom || isSamePage) {
                return;
            }

            const modalSearchParam = pathToSearchParam(toPath.pathname);

            const nextLocation = router.buildLocation({
                to: fallbackFrom.pathname,
                search: { ...toPath.search, modal: modalSearchParam },
            });

            await router.commitLocation({
                ...nextLocation,
                replace: true,
            });
        },
        [router, openModalIfRequired],
    );

    // TODO: check if this logic can be moved to useScrollToLocation hook
    const handleScroll = useCallback(
        (route: IStaticBlocksData) => {
            const timeoutDuration = 2000;
            const startTime = performance.now();

            const attemptScroll = () => {
                const scrollTarget = route.autoScrollOptions?.targetElement;
                const targetElement = scrollTarget
                    ? (document.querySelector(scrollTarget) as HTMLElement)
                    : document.body;

                if (targetElement) {
                    scrollIntoView({ ...route.autoScrollOptions, targetElement });
                } else if (performance.now() - startTime < timeoutDuration) {
                    requestAnimationFrame(attemptScroll);
                } else {
                    console.warn(
                        `Scroll target '${scrollTarget}' not found. Defaulting to 'document.body'.`,
                    );
                    scrollIntoView({ ...route.autoScrollOptions, targetElement: document.body });
                }
            };

            requestAnimationFrame(attemptScroll);
        },
        [scrollIntoView],
    );

    const handleOnResolved = useCallback(
        async (event: RouterEvents['onBeforeLoad']) => {
            const { toLocation, fromLocation } = event;
            const { pathname: toPath, search } = toLocation as ParsedLocation<UrlSearchSchema>;
            const route = getRoute(toPath, search);

            const isSystemRoute = route?.isSystemRoute ?? false;
            const isModalRoute = search.modal || route?.openInModal;

            // short circuit if route is modal based
            if (route?.openInModal) {
                await handleModalRouting(toLocation, fromLocation);
                return;
            }

            if (search.modal) {
                openModalIfRequired(toLocation.href);
            }

            // preserve scroll for when we don't want to scroll to some target.
            // ie. handleScroll will still scroll to document.body if no target is found, hence this check.
            if (route && !route.preserveScroll) {
                handleScroll(route);
            }

            const { closeModal, isOpen } = useModalViewStore.getState();
            if (!Object.keys(toLocation.search).includes('modal') && isOpen) {
                closeModal();
            }

            if (toPath && !isSystemRoute && !isModalRoute) {
                setPreviousPath(toPath);
                setPreviousLocation({
                    pathname: toPath,
                    search: search as unknown as Record<string, string>,
                    hash: toLocation.hash,
                });
            }
        },
        [
            getRoute,
            handleModalRouting,
            handleScroll,
            setPreviousPath,
            setPreviousLocation,
            openModalIfRequired,
        ],
    );

    useLayoutEffect(() => {
        openModalIfRequired(window.location.href);

        const unsubscribeOnBeforeLoad = router.subscribe('onBeforeLoad', handleOnResolved);

        return () => {
            unsubscribeOnBeforeLoad();
        };
    }, [router, handleOnResolved, openModalIfRequired]);
};
